/*!

 * DH Commerce OS v1.4

 * dh-commerce-bootstrap.js

 *

 * Safe startup and orchestration layer for DH Commerce OS.

 * Verifies dependencies, hydrates state, registers configured definitions,

 * starts engines in order, binds lifecycle events, and fails gracefully.

 */

(function (window, document) {

  "use strict";

 

  var ROOT = window.DHCommerce = window.DHCommerce || {};

  var VERSION = "1.4.0";

  var ENGINE_ORDER = [

    "rules",

    "offers",

    "personalization",

    "recommendations",

    "experiments",

    "ui",

    "analytics"

  ];

 

  var status = {

    version: VERSION,

    phase: "idle",

    started: false,

    starting: false,

    stopped: false,

    healthy: true,

    startedAt: null,

    completedAt: null,

    durationMs: null,

    storeId: null,

    engines: {},

    warnings: [],

    errors: [],

    registrations: {},

    listeners: []

  };

 

  function now() { return Date.now(); }

  function noop() {}

  function isFunction(value) { return typeof value === "function"; }

  function isObject(value) { return value !== null && typeof value === "object"; }

  function isPromise(value) { return value && isFunction(value.then); }

 

  function copy(value) {

    if (Array.isArray(value)) return value.map(copy);

    if (isObject(value)) {

      var output = {};

      Object.keys(value).forEach(function (key) { output[key] = copy(value[key]); });

      return output;

    }

    return value;

  }

 

  function config(path, fallback) {

    if (ROOT.config && isFunction(ROOT.config.get)) return ROOT.config.get(path, fallback);

    return fallback;

  }

 

  function debugEnabled() { return !!config("runtime.debug", false); }

 

  function log(level, message, data) {

    var logger = ROOT.core && ROOT.core.logger;

    if (logger && isFunction(logger[level])) {

      logger[level](message, data);

      return;

    }

    if (level === "debug" && !debugEnabled()) return;

    if (window.console && isFunction(window.console[level])) {

      window.console[level]("[DHCommerce] " + message, data === undefined ? "" : data);

    }

  }

 

  function emit(name, detail) {

    var payload = detail || {};

    payload.bootstrapVersion = VERSION;

    try {

      if (ROOT.events && isFunction(ROOT.events.emit)) {

        ROOT.events.emit(name, payload);

      } else {

        document.dispatchEvent(new CustomEvent(name, { detail: payload }));

      }

    } catch (error) {

      log("warn", "Unable to emit event " + name, error);

    }

  }

 

  function listen(target, name, handler, options) {

    if (!target || !isFunction(target.addEventListener)) return noop;

    target.addEventListener(name, handler, options || false);

    var unsubscribe = function () {

      target.removeEventListener(name, handler, options || false);

    };

    status.listeners.push(unsubscribe);

    return unsubscribe;

  }

 

  function recordError(code, message, error, fatal) {

    var entry = {

      code: code,

      message: message,

      fatal: !!fatal,

      timestamp: new Date().toISOString(),

      error: error ? String(error.message || error) : null

    };

    status.errors.push(entry);

    if (fatal) status.healthy = false;

    log(fatal ? "error" : "warn", message, error || entry);

    emit("dhcommerce:error", entry);

    runHooks("onError", entry).catch(noop);

    return entry;

  }

 

  function recordWarning(code, message, data) {

    var entry = { code: code, message: message, timestamp: new Date().toISOString(), data: data || null };

    status.warnings.push(entry);

    log("warn", message, data);

    emit("dhcommerce:warning", entry);

    return entry;

  }

 

  function withTimeout(value, timeoutMs, label) {

    var promise = isPromise(value) ? value : Promise.resolve(value);

    if (!timeoutMs || timeoutMs <= 0) return promise;

    return new Promise(function (resolve, reject) {

      var timer = window.setTimeout(function () {

        reject(new Error((label || "Operation") + " timed out after " + timeoutMs + "ms"));

      }, timeoutMs);

      promise.then(function (result) {

        window.clearTimeout(timer);

        resolve(result);

      }, function (error) {

        window.clearTimeout(timer);

        reject(error);

      });

    });

  }

 

  function runHooks(name, context) {

    var hooks = config("hooks." + name, []);

    if (!Array.isArray(hooks)) hooks = [hooks];

    return hooks.reduce(function (chain, hook) {

      return chain.then(function () {

        if (!isFunction(hook)) return null;

        return hook(context, ROOT);

      });

    }, Promise.resolve());

  }

 

  function resolveEngine(name) {

    var aliases = {

      rules: ["ruleEngine", "rules"],

      offers: ["offerEngine", "offers"],

      personalization: ["personalizationEngine", "personalization"],

      recommendations: ["recommendationEngine", "recommendations"],

      experiments: ["experimentEngine", "experiments"],

      ui: ["ui"],

      analytics: ["analytics", "analyticsService"]

    };

    var candidates = aliases[name] || [name];

    for (var i = 0; i < candidates.length; i += 1) {

      if (ROOT[candidates[i]]) return ROOT[candidates[i]];

    }

    return null;

  }

 

  function engineSettings(name) {

    return config("engines." + name, { enabled: true, required: false }) || {};

  }

 

  function checkDependencies() {

    status.phase = "dependencies";

    var validation = ROOT.config && isFunction(ROOT.config.validate)

      ? ROOT.config.validate()

      : { valid: false, errors: ["Configuration API is unavailable"], warnings: [] };

 

    (validation.warnings || []).forEach(function (message) {

      recordWarning("CONFIG_WARNING", message);

    });

 

    if (!validation.valid) {

      (validation.errors || []).forEach(function (message) {

        recordError("CONFIG_INVALID", message, null, true);

      });

    }

 

    ENGINE_ORDER.forEach(function (name) {

      var settings = engineSettings(name);

      var engine = resolveEngine(name);

      status.engines[name] = {

        enabled: settings.enabled !== false,

        required: !!settings.required,

        present: !!engine,

        initialized: false,

        healthy: true,

        version: engine && engine.version ? engine.version : null,

        error: null

      };

      if (settings.enabled !== false && !engine) {

        var fatal = !!settings.required || !!config("runtime.strictDependencies", false);

        recordError("ENGINE_MISSING", "Engine not found: " + name, null, fatal);

        status.engines[name].healthy = !fatal;

      }

    });

 

    if (!validation.valid && !config("runtime.failOpen", true)) {

      throw new Error("DH Commerce configuration validation failed.");

    }

    return copy(status.engines);

  }

 

  function hydrateState() {

    status.phase = "hydration";

    emit("dhcommerce:before-hydrate", { status: snapshot() });

    return runHooks("beforeHydrate", snapshot()).then(function () {

      var state = ROOT.state = ROOT.state || {};

      state.runtime = state.runtime || {};

      state.runtime.storeId = config("meta.id", "unknown-store");

      state.runtime.version = VERSION;

      state.runtime.startedAt = new Date().toISOString();

      state.runtime.environment = config("meta.environment", "production");

      state.runtime.currency = config("meta.currency", "USD");

      state.runtime.locale = config("meta.locale", "en-US");

 

      var campaignParams = config("campaigns.parameters", []);

      var query = new URLSearchParams(window.location.search || "");

      state.campaign = state.campaign || {};

      campaignParams.forEach(function (parameter) {

        var value = query.get(parameter);

        if (value !== null) state.campaign[parameter] = value;

      });

 

      state.page = {

        url: window.location.href,

        path: window.location.pathname,

        title: document.title,

        referrer: document.referrer || null,

        deviceType: window.innerWidth <= 767 ? "mobile" : (window.innerWidth <= 1024 ? "tablet" : "desktop")

      };

 

      emit("dhcommerce:after-hydrate", { state: copy(state) });

      return runHooks("afterHydrate", copy(state));

    });

  }

 

  function callRegistration(engine, method, definitions, label) {

    if (!definitions || !definitions.length || !engine) return Promise.resolve(0);

    try {

      if (isFunction(engine[method])) {

        var result = engine[method](definitions);

        return Promise.resolve(result).then(function () { return definitions.length; });

      }

      if (method === "registerMany" && isFunction(engine.register)) {

        definitions.forEach(function (definition) { engine.register(definition); });

        return Promise.resolve(definitions.length);

      }

      recordWarning("REGISTER_UNSUPPORTED", label + " engine does not expose " + method);

      return Promise.resolve(0);

    } catch (error) {

      recordError("REGISTER_FAILED", "Registration failed for " + label, error, false);

      return Promise.resolve(0);

    }

  }

 

  function registerConfiguration() {

    status.phase = "registration";

    var tasks = [];

 

    var rules = config("rules", []);

    var offers = config("offers", []);

    var recommendations = config("recommendations", []);

    var experiments = config("experiments", []);

    var catalog = config("catalog", {});

    var placements = config("placements.definitions", []);

 

    tasks.push(callRegistration(resolveEngine("rules"), "registerMany", rules, "rules").then(function (n) { status.registrations.rules = n; }));

    tasks.push(callRegistration(resolveEngine("offers"), "registerMany", offers, "offers").then(function (n) { status.registrations.offers = n; }));

    tasks.push(callRegistration(resolveEngine("recommendations"), "registerMany", recommendations, "recommendations").then(function (n) { status.registrations.recommendations = n; }));

    tasks.push(callRegistration(resolveEngine("experiments"), "registerMany", experiments, "experiments").then(function (n) { status.registrations.experiments = n; }));

 

    var recommendationEngine = resolveEngine("recommendations");

    if (recommendationEngine) {

      if (Array.isArray(catalog.products) && isFunction(recommendationEngine.registerProducts)) {

        tasks.push(Promise.resolve(recommendationEngine.registerProducts(catalog.products)).then(function () {

          status.registrations.products = catalog.products.length;

        }));

      }

      if (Array.isArray(catalog.categories) && isFunction(recommendationEngine.registerCategories)) {

        tasks.push(Promise.resolve(recommendationEngine.registerCategories(catalog.categories)).then(function () {

          status.registrations.categories = catalog.categories.length;

        }));

      }

    }

 

    var ui = resolveEngine("ui");

    if (ui && Array.isArray(placements)) {

      if (isFunction(ui.registerPlacements)) {

        tasks.push(Promise.resolve(ui.registerPlacements(placements)).then(function () { status.registrations.placements = placements.length; }));

      } else if (isFunction(ui.registerPlacement)) {

        placements.forEach(function (placement) { ui.registerPlacement(placement); });

        status.registrations.placements = placements.length;

      }

    }

 

    return Promise.all(tasks).then(function () {

      emit("dhcommerce:configuration-registered", { registrations: copy(status.registrations) });

      return copy(status.registrations);

    });

  }

 

  function initializeEngine(name) {

    var settings = engineSettings(name);

    var engine = resolveEngine(name);

    var engineStatus = status.engines[name];

    if (!engineStatus) return Promise.resolve(null);

    if (settings.enabled === false) {

      engineStatus.skipped = true;

      return Promise.resolve(null);

    }

    if (!engine) return Promise.resolve(null);

 

    var method = isFunction(engine.init) ? "init" : (isFunction(engine.start) ? "start" : null);

    if (!method) {

      engineStatus.initialized = true;

      return Promise.resolve(engine);

    }

 

    var context = {

      config: ROOT.config && isFunction(ROOT.config.snapshot) ? ROOT.config.snapshot() : null,

      state: ROOT.state,

      root: ROOT,

      bootstrap: API

    };

 

    emit("dhcommerce:engine-before-init", { engine: name });

    var startedAt = now();

    var timeout = settings.timeoutMs || config("runtime.engineTimeoutMs", 5000);

 

    return withTimeout(engine[method](context), timeout, "Engine " + name).then(function () {

      engineStatus.initialized = true;

      engineStatus.durationMs = now() - startedAt;

      engineStatus.version = engine.version || engineStatus.version;

      emit("dhcommerce:engine-ready", { engine: name, durationMs: engineStatus.durationMs });

      return engine;

    }).catch(function (error) {

      engineStatus.initialized = false;

      engineStatus.healthy = false;

      engineStatus.error = String(error.message || error);

      var fatal = !!settings.required && !config("runtime.failOpen", true);

      recordError("ENGINE_INIT_FAILED", "Engine initialization failed: " + name, error, fatal);

      if (fatal) throw error;

      return null;

    });

  }

 

  function startEngines() {

    status.phase = "engines";

    return ENGINE_ORDER.reduce(function (chain, name) {

      return chain.then(function () { return initializeEngine(name); });

    }, Promise.resolve());

  }

 

  function bindRuntimeEvents() {

    status.phase = "events";

 

    listen(document, "shopify:section:load", function (event) {

      if (!config("runtime.rehydrateOnShopifySectionLoad", true)) return;

      var ui = resolveEngine("ui");

      if (ui && isFunction(ui.hydrate)) {

        try { ui.hydrate(event.target || document); }

        catch (error) { recordError("SECTION_HYDRATE_FAILED", "Shopify section hydration failed", error, false); }

      }

      emit("dhcommerce:shopify-section-loaded", { sectionId: event.detail && event.detail.sectionId });

    });

 

    listen(document, "visibilitychange", function () {

      emit("dhcommerce:visibility-changed", { hidden: document.hidden });

    });

 

    listen(window, "online", function () { emit("dhcommerce:network", { online: true }); });

    listen(window, "offline", function () { emit("dhcommerce:network", { online: false }); });

    listen(window, "pageshow", function (event) {

      if (event.persisted) {

        var ui = resolveEngine("ui");

        if (ui && isFunction(ui.hydrate)) ui.hydrate(document);

        emit("dhcommerce:page-restored", { persisted: true });

      }

    });

 

    return true;

  }

 

  function start() {

    if (status.started) return Promise.resolve(snapshot());

    if (status.starting) return API.ready;

    if (!config("runtime.enabled", true)) {

      status.phase = "disabled";

      return Promise.resolve(snapshot());

    }

 

    status.starting = true;

    status.stopped = false;

    status.startedAt = now();

    status.storeId = config("meta.id", "unknown-store");

    status.phase = "starting";

 

    emit("dhcommerce:before-start", { status: snapshot() });

 

    var workflow = runHooks("beforeStart", snapshot())

      .then(checkDependencies)

      .then(hydrateState)

      .then(registerConfiguration)

      .then(startEngines)

      .then(bindRuntimeEvents)

      .then(function () {

        status.started = true;

        status.starting = false;

        status.phase = "ready";

        status.completedAt = now();

        status.durationMs = status.completedAt - status.startedAt;

        ROOT.state = ROOT.state || {};

        ROOT.state.runtime = ROOT.state.runtime || {};

        ROOT.state.runtime.ready = true;

        ROOT.state.runtime.bootstrapDurationMs = status.durationMs;

        emit("dhcommerce:ready", { status: snapshot() });

        return runHooks("afterStart", snapshot()).then(function () { return snapshot(); });

      })

      .catch(function (error) {

        status.starting = false;

        status.phase = "failed";

        status.completedAt = now();

        status.durationMs = status.completedAt - status.startedAt;

        recordError("BOOTSTRAP_FAILED", "DH Commerce bootstrap failed", error, true);

        if (!config("runtime.failOpen", true)) throw error;

        return snapshot();

      });

 

    API.ready = withTimeout(workflow, config("runtime.startupTimeoutMs", 10000), "DH Commerce startup").catch(function (error) {

      status.starting = false;

      status.phase = "timeout";

      recordError("BOOTSTRAP_TIMEOUT", "DH Commerce startup timed out", error, true);

      if (!config("runtime.failOpen", true)) throw error;

      return snapshot();

    });

 

    return API.ready;

  }

 

  function stop() {

    status.phase = "stopping";

    while (status.listeners.length) {

      try { status.listeners.pop()(); } catch (error) { log("warn", "Listener cleanup failed", error); }

    }

 

    return ENGINE_ORDER.slice().reverse().reduce(function (chain, name) {

      return chain.then(function () {

        var engine = resolveEngine(name);

        if (!engine) return null;

        var method = isFunction(engine.destroy) ? "destroy" : (isFunction(engine.stop) ? "stop" : null);

        if (!method) return null;

        try { return engine[method](); }

        catch (error) { recordError("ENGINE_STOP_FAILED", "Engine shutdown failed: " + name, error, false); }

        return null;

      });

    }, Promise.resolve()).then(function () {

      status.started = false;

      status.starting = false;

      status.stopped = true;

      status.phase = "stopped";

      if (ROOT.state && ROOT.state.runtime) ROOT.state.runtime.ready = false;

      emit("dhcommerce:stopped", { status: snapshot() });

      return snapshot();

    });

  }

 

  function restart() {

    return stop().then(function () {

      status.errors = [];

      status.warnings = [];

      status.healthy = true;

      status.engines = {};

      status.registrations = {};

      return start();

    });

  }

 

  function snapshot() {

    var publicStatus = copy(status);

    publicStatus.listeners = status.listeners.length;

    return publicStatus;

  }

 

  function health() {

    var engines = {};

    Object.keys(status.engines).forEach(function (name) {

      var item = status.engines[name];

      engines[name] = {

        enabled: item.enabled,

        present: item.present,

        initialized: item.initialized,

        healthy: item.healthy,

        version: item.version,

        durationMs: item.durationMs || null

      };

    });

    return {

      healthy: status.healthy && status.errors.filter(function (entry) { return entry.fatal; }).length === 0,

      phase: status.phase,

      storeId: status.storeId,

      version: VERSION,

      durationMs: status.durationMs,

      engines: engines,

      warningCount: status.warnings.length,

      errorCount: status.errors.length

    };

  }

 

  function diagnostics() {

    var report = {

      bootstrap: snapshot(),

      health: health(),

      configuration: ROOT.config && isFunction(ROOT.config.validate) ? ROOT.config.validate() : null,

      state: copy(ROOT.state || {}),

      location: { href: window.location.href, userAgent: window.navigator.userAgent },

      timestamp: new Date().toISOString()

    };

    log("info", "Diagnostics", report);

    return report;

  }

 

  var API = {

    version: VERSION,

    ready: Promise.resolve(null),

    start: start,

    stop: stop,

    restart: restart,

    health: health,

    diagnostics: diagnostics,

    status: snapshot,

    checkDependencies: checkDependencies,

    hydrate: hydrateState,

    registerConfiguration: registerConfiguration,

    startEngines: startEngines,

    emit: emit

  };

   /* ======================================================
     REMOTE ANALYTICS BRIDGE
     Connects the modular DH Commerce event bus to Supabase.
     ====================================================== */

  function installAnalyticsBridge() {
    if (
      ROOT.analytics &&
      typeof ROOT.analytics.track === "function" &&
      ROOT.analytics.__dhModularBridge === true
    ) {
      return ROOT.analytics;
    }

    var collectorEndpoint =
      config(
        "analytics.endpoint",
        "https://ovcqgurzercvxvjlijnz.supabase.co/functions/v1/collect-commerce-event"
      );

    var sequenceKey = "dhcommerce:analytics-sequence";
    var visitorKey = "dhcommerce:visitor-id";
    var sessionKey = "dhcommerce:session-id";

    function createId(prefix) {
      if (
        window.crypto &&
        typeof window.crypto.randomUUID === "function"
      ) {
        return prefix + "_" + window.crypto.randomUUID();
      }

      return (
        prefix +
        "_" +
        Date.now().toString(36) +
        "_" +
        Math.random().toString(36).slice(2)
      );
    }

    function storageGet(storage, key) {
      try {
        return storage ? storage.getItem(key) : null;
      } catch (error) {
        return null;
      }
    }

    function storageSet(storage, key, value) {
      try {
        if (storage) storage.setItem(key, value);
      } catch (error) {
        /* Storage can be unavailable in privacy mode. */
      }
    }

    function getVisitorId() {
      var existing =
        storageGet(window.localStorage, visitorKey);

      if (existing) return existing;

      var visitorId = createId("visitor");

      storageSet(
        window.localStorage,
        visitorKey,
        visitorId
      );

      return visitorId;
    }

    function getSessionId() {
      var existing =
        storageGet(window.sessionStorage, sessionKey);

      if (existing) return existing;

      var sessionId = createId("session");

      storageSet(
        window.sessionStorage,
        sessionKey,
        sessionId
      );

      return sessionId;
    }

    function nextSequence() {
      var current = Number(
        storageGet(window.sessionStorage, sequenceKey) || 0
      );

      var next = current + 1;

      storageSet(
        window.sessionStorage,
        sequenceKey,
        String(next)
      );

      return next;
    }

    function getShippingTier() {
      var shipping =
        ROOT.state &&
        ROOT.state.shipping
          ? ROOT.state.shipping
          : {};

      return (
        shipping.effectiveTier ||
        shipping.tier ||
        null
      );
    }

    function getDeviceType() {
      if (window.innerWidth <= 767) {
        return "mobile";
      }

      if (window.innerWidth <= 1024) {
        return "tablet";
      }

      return "desktop";
    }

    function send(name, payload, eventOptions) {
      if (!collectorEndpoint) {
        return Promise.resolve(null);
      }

      var occurredAt =
        eventOptions && eventOptions.timestamp
          ? eventOptions.timestamp
          : new Date().toISOString();

      var remoteEvent = {
        event_id: createId("event"),
        event_name: String(name || "unknown"),
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        sequence_number: nextSequence(),
        occurred_at: occurredAt,
        framework_version:
          ROOT.version || VERSION,

        page_url: window.location.href,
        page_path: window.location.pathname,
        landing_page:
          window.location.pathname +
          window.location.search,
        referrer: document.referrer || null,

        traffic_source:
          new URLSearchParams(
            window.location.search
          ).get("utm_source") || null,

        shipping_tier: getShippingTier(),
        shipping_effective_tier:
          getShippingTier(),

        device_type: getDeviceType(),

        payload: payload || {}
      };

      return fetch(collectorEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(remoteEvent),
        keepalive: true
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error(
              "Analytics collector returned " +
              response.status
            );
          }

          return response
            .json()
            .catch(function () {
              return { success: true };
            });
        })
        .catch(function (error) {
          log(
            "warn",
            "Remote analytics upload failed",
            error
          );

          return null;
        });
    }

    ROOT.analytics = {
      name: "analytics",
      version: VERSION,
      initialized: true,
      started: true,
      __dhModularBridge: true,

      /*
       * Core calls this automatically for every event.
       * Homepage code may also call it directly.
       *
       * Do not call ROOT.events.emit() here. Doing so
       * would create a forwarding loop and duplicates.
       */
      track: function (name, payload, eventOptions) {
        return send(
          name,
          payload || {},
          eventOptions || {}
        );
      },

      send: send,

      getHealth: function () {
        return {
          name: "analytics",
          initialized: true,
          healthy: !!collectorEndpoint,
          status: collectorEndpoint
            ? "ready"
            : "disabled",
          endpointConfigured:
            !!collectorEndpoint
        };
      }
    };

    if (
      ROOT.engines &&
      typeof ROOT.engines.register === "function"
    ) {
      try {
        ROOT.engines.register(
          "analytics",
          ROOT.analytics,
          {
            version: VERSION,
            source: "bootstrap"
          }
        );
      } catch (error) {
        log(
          "warn",
          "Analytics engine registration skipped",
          error
        );
      }
    }

    return ROOT.analytics;
  }

  installAnalyticsBridge();

  ROOT.bootstrap = API;

 

  function scheduleAutoStart() {

    if (!config("runtime.autoStart", true)) return;

    var eventName = config("runtime.startupEvent", "DOMContentLoaded");

    if (eventName === "immediate" || document.readyState === "interactive" || document.readyState === "complete") {

      window.setTimeout(start, 0);

    } else {

      document.addEventListener(eventName, start, { once: true });

    }

  }

 

  emit("dhcommerce:bootstrap-loaded", { version: VERSION });

  scheduleAutoStart();

 

})(window, document);